import { IGif } from '@giphy/js-types'
import { Gif } from '@giphy/react-components'
import { Video } from '@giphy/react-giphy-brand'
import { VideoPreviewLabel } from '@giphy/react-giphy-brand'
import React, { SyntheticEvent, useContext, useState, useCallback } from 'react'
import { GridOverlayContext } from '../grid-overlay'
import GifSelectionOverlay from '../gif-selector-overaly'
import { useHistory } from 'react-router'
import PlayBoxIcon from 'shared/components/icons/video/play'
import { useVideoAnalytics } from 'shared/components/video-player/analytics-hook'
import { mobile } from 'shared/util/media-query'
import styled, { css } from 'styled-components'
import ModalContext from 'shared/contexts/modal'
import UserContext from 'shared/contexts/user'
import { isPlaceholder } from '../gif-skeleton'
import { EditButton } from './edit-button'
import Lock from 'shared/components/icons/lock'
import { giphyRed } from '@giphy/js-brand'
import { parseUrl } from 'shared/util/url'
import { ClipboardButton } from 'shared/components/gif-menu-overlay'

export const ASPECT_RATIO = 16 / 9

const Container = styled.div<{ isPlaceholder: boolean }>`
    align-items: center;
    display: flex;
    flex-direction: column;

    ${(props) =>
        props.isPlaceholder &&
        css`
            visibility: hidden;
            pointer-events: none;
        `}
`

const OverlayContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    height: 100%;
    width: 100%;
    opacity: 0;
`

const Private = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    fill: ${giphyRed};
    background: rgba(0, 0, 0, 0.5);
    pointer-events: none;
    transition: opacity 0.2s ease-out 0s;
`

const PreviewContainer = styled.div`
    position: relative;
    .${Gif.className} {
        img {
            object-fit: cover;
        }
    }
    display: flex;

    &:hover {
        ${OverlayContainer} {
            opacity: 1;
        }

        ${Private} {
            opacity: 0;
        }
    }
`
const StyledPreviewLabel = styled(VideoPreviewLabel)`
    margin-left: auto;
    margin-right: auto;

    h4 {
        font-size: 17px;
        font-weight: bold;
    }
`

const PlayIcon = styled(PlayBoxIcon)`
    position: absolute;
    bottom: 10px;
    left: 10px;
`

const StyledEditButton = styled(EditButton)`
    position: absolute;
    bottom: 6px;
    right: 6px;

    ${mobile.css`
        display: none;
    `}
`

const ClipboardButtonDiv = styled.div`
    position: absolute;
    top: 0;
    right: 6px;
    ${mobile.css`
        display: none;
    `}
`

type Props = {
    gif: IGif
    width: number
    height?: number
    className?: string
    hideAttribution?: boolean
    editButtonSpriteUrl?: string // for storybook only
    labelPadding?: number
    canPlayInline?: boolean
    preventDefault?: boolean
    hideLabel?: boolean
    playButtonSize?: { height: number; width: number }
    playButtonAnimated?: boolean
}

const VideoPreview = ({
    gif,
    width,
    height,
    editButtonSpriteUrl,
    hideAttribution = false,
    className = '',
    labelPadding = 0,
    canPlayInline = false,
    preventDefault = true,
    hideLabel = false,
    playButtonSize = undefined,
    playButtonAnimated = false,
}: Props) => {
    const [isPlayingInline, setIsPlayingInline] = useState(false)
    const events = useVideoAnalytics(gif.id)
    const { user: loggedInUser } = useContext(UserContext)
    const { openGifEditPanel } = useContext(ModalContext)
    const { onGifClick, selectedGifs, iSelectionMode } = useContext(GridOverlayContext)
    const history = useHistory()
    const { title, user } = gif
    const canEditGif = (loggedInUser && loggedInUser.isStaff) || (loggedInUser && user && user.id === loggedInUser.id)
    const previewHeight = height || width / ASPECT_RATIO
    const { pathname } = parseUrl(gif.url)
    const onEditButtonClicked = (evt: SyntheticEvent<HTMLElement, Event>) => {
        evt.preventDefault()
        openGifEditPanel(gif)
    }
    const onPlayButtonClicked = () => {
        if (canPlayInline) {
            setIsPlayingInline(true)
        }
    }
    const PlayButton = useCallback(() => {
        if (!canPlayInline) {
            return (
                <a href={String(pathname)}>
                    <PlayIcon onClick={onPlayButtonClicked} size={playButtonSize} isAnimated={playButtonAnimated} />
                </a>
            )
        }
        return <PlayIcon onClick={onPlayButtonClicked} size={playButtonSize} isAnimated={playButtonAnimated} />
    }, [pathname, onPlayButtonClicked, playButtonSize, canPlayInline])
    const HoverOverlay = () => (
        <OverlayContainer>
            {canEditGif && !iSelectionMode && (
                <StyledEditButton background={editButtonSpriteUrl} onClick={onEditButtonClicked} />
            )}
            <ClipboardButtonDiv>
                <ClipboardButton gif={gif} isClip={true} />
            </ClipboardButtonDiv>
        </OverlayContainer>
    )
    const Overlay = () => (
        <>
            <PlayButton />
            {!!gif.is_hidden && (
                <Private>
                    <Lock />
                </Private>
            )}
            {iSelectionMode && (
                <GifSelectionOverlay gif={gif} index={selectedGifs.indexOf(gif.id)} onClick={onGifClick} />
            )}
        </>
    )

    return (
        <Container className={className} isPlaceholder={!!gif[isPlaceholder]}>
            <PreviewContainer>
                {isPlayingInline ? (
                    <Video gif={gif} width={width} height={height} autoPlay events={events} noCutout noShare />
                ) : (
                    <>
                        <Gif
                            gif={gif}
                            width={width}
                            onGifClick={(_, e) => {
                                if (preventDefault) {
                                    e.preventDefault()
                                    history.push(String(pathname))
                                }
                            }}
                            height={previewHeight}
                            overlay={HoverOverlay}
                        />
                        <Overlay />
                    </>
                )}
            </PreviewContainer>
            {!hideLabel && (
                <StyledPreviewLabel
                    href={gif.url}
                    onClick={() => history.push(String(pathname))}
                    showAttribution={!hideAttribution}
                    title={title}
                    user={user}
                    width={width - labelPadding * 2}
                />
            )}
        </Container>
    )
}

export default VideoPreview
